import React, { useEffect, useRef, useState } from "react";
import { useAdvancedValidation } from "hooks/useAdvancedValidation";
import MessageSnackBar from "components/widgets/message-snackbar/MessageSnackBar";
import "./Alert.css";
import MainLayout from "components/layout/main-layout/MainLayout";
import { titles } from "constants/titles";
import { ArrowRight, IconInfo, EyeFill, DeviceIdsIcon } from "assets/SVGs/SVGs";
import NextPageLoaderIcon from "assets/SVGs/NextPageLoaderIcon";
import InformationModal from "components/layout/information-modal/InformationModal";
import DeviceIdsModal from "components/layout/device-ids-modal/DeviceIdsModal";
import { useDispatch, useSelector } from "react-redux";
import {
  getNotificationList,
  markAllAsReadNotification,
  markAsReadNotification,
  markReadLocaly,
} from "redux/Notifications/actions";
import { useHistory } from "react-router-dom";
import { setSelectedDeviceDetails } from "redux/Devices/actions";
import alerts from "constants/alerts";
import { convertInRequiredTimeZone } from "utility";
import { getCustomerTimeZones } from "redux/Common/action";

const Alert = () => {
  const userData = useSelector((store) => store?.auth?.userData);
  const {
    notificationList,
    loading,
    notificationListCount,
    enableNext,
    totalUnreadCount,
  } = useSelector((store) => store?.notifications);
  const { medicalUserTimezone } = useSelector((store) => store?.common);

  const dispatch = useDispatch();
  const [fetchingMore, setFetchingMore] = useState(false);
  const loaderRef = useRef(null);
  const history = useHistory();
  const containerRef = useRef(null);
  const prevLenRef = useRef(notificationList?.length || 0);

  const { onApiResponse, snackbarState, dismissSnackbar } = useAdvancedValidation();

  useEffect(() => {
    getCustomerTimeZones({
      dispatch,
      userType: userData?.isGuestUser ? "guestuser" : "accountowner",
    });
    getList();
  }, []);

  const getList = (fetchMore = false) => {
    let params = {
      userType: userData?.isGuestUser ? "guestuser" : "accountowner",
      history,
      dispatch,
      fetchMore,
      page: fetchMore ? notificationList?.length / 10 + 1 : 1,
    };
    getNotificationList(params, false, medicalUserTimezone);
  };

  const handleScroll = () => {
    const container = containerRef?.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isBottom = scrollTop + clientHeight >= scrollHeight - 20;
      if (isBottom) {
        if (
          notificationListCount > notificationList?.length &&
          enableNext &&
          !loading &&
          !fetchingMore
        ) {
          setFetchingMore(true);
          getList(true);
        }
      }
    }
  };

  // Hide bottom loader once new items are appended or no more pages remain
  useEffect(() => {
    const currentLen = notificationList?.length || 0;
    if (currentLen !== prevLenRef?.current) {
      prevLenRef.current = currentLen;
      setFetchingMore(false);
    } else if (
      fetchingMore &&
      ((notificationListCount || 0) <= currentLen || !enableNext)
    ) {
      setFetchingMore(false);
    }
  }, [notificationList, notificationListCount, enableNext, fetchingMore]);

  const onNotificationClick = (notif) => {
    let params = {
      userType: userData?.isGuestUser ? "guestuser" : "accountowner",
      history,
      dispatch,
      notification_id: notif?.notification_id,
    };
    setSelectedDeviceDetails(dispatch, {
      ...notif,
    });
    markAsReadNotification(params, medicalUserTimezone, onApiResponse);
  };

  const handleOnAllReadNotification = () => {
    if (totalUnreadCount <= 0) {
      onApiResponse(titles?.errorUnreadMessage, "success");
      return;
    }
    markReadLocaly(dispatch, notificationList);
    const payload = {
      userType: userData?.isGuestUser ? "guestuser" : "accountowner",
    };
    markAllAsReadNotification(payload, onApiResponse);
  };

  return (
    <>
      <MessageSnackBar
        message={snackbarState?.message}
        isVisible={snackbarState?.show}
        onDismiss={dismissSnackbar}
        type={snackbarState?.type}
        autoHideDuration={snackbarState?.autoHideDuration}
      />
      <MainLayout
        text={titles?.alerts}
        isBackHide={true}
        isSemiHeader={true}
        white={true}
        isLoading={loading && (notificationList?.length || 0) === 0 && !fetchingMore}
        isScrollHide={true}
        fullWidthScreen={true}
      >
        <div className="alert_main_container">
          {/* Message Center Header */}
          <div className="alert_message_center_header">
            <h2 className="alert_message_center_title">Message Center</h2>
            <button
              onClick={handleOnAllReadNotification}
              className={`alert_mark_all_read_btn ${totalUnreadCount > 0
                ? "alert_mark_all_read_enabled"
                : "alert_mark_all_read_disabled"
                }`}
            >
              {titles?.markReadAll}
            </button>
          </div>

          {/* Alert Cards Container */}
          <div
            ref={containerRef}
            onScroll={handleScroll}
            className="alert_cards_container"
          >
            {notificationList && notificationList?.length > 0 ? (
              notificationList?.map((item, index) => {
                return (
                  <AlertCard
                    item={item}
                    index={index}
                    onNotificationClick={() => onNotificationClick(item)}
                  />
                );
              })
            ) : (
              <p className="alert_empty_message">{titles?.empty_notification}</p>
            )}
            {/* Sentinel after last item to detect end of list */}
            <div ref={loaderRef} />
            {fetchingMore && (
              <div className="alert_paging_loader">
                <NextPageLoaderIcon />
              </div>
            )}
          </div>
        </div>
      </MainLayout>
    </>
  );
};

const AlertCard = ({ item, index, onNotificationClick }) => {
  const [show, setShow] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [openDeviceIdsModal, setOpenDeviceIdsModal] = useState(false);
  const { currentTimeZone } = useSelector((store) => store?.common);

  function processString(isTrue, str) {
    if (isTrue) {
      return str?.slice(10);
    }
    return str;
  }

  return (
    <div className={`alert_card_wrapper ${show ? "alert_card_swiped" : ""}`}>
      <InformationModal
        show={openInfoModal}
        setShow={setOpenInfoModal}
        infoText={item?.alarm_code_tips || alerts?.empty_info}
      />
      <DeviceIdsModal
        show={openDeviceIdsModal}
        setShow={setOpenDeviceIdsModal}
        notification={item}
      />

      {/* Main Alert Card */}
      <div
        onClick={() => setShow(!show)}
        className={`alert_card_container ${show ? "swiped_card_effect" : ""}`}
      >
        <div className="alert_card_content">
          <div className="alert_dot_section">
            <div
              className={`alert_status_dot ${item?.is_read ? "alert_dot_read" : "alert_dot_unread"
                }`}
            ></div>
          </div>

          <div className="alert_text_section">
            <h3 className="alert_title">{item?.notification_title}</h3>
            <p className="alert_subscriber">
              {processString(
                show,
                `${titles?.subscriber}: ${item?.firstname + " " + item?.lastname
                }`
              )}
            </p>
            <p className="alert_date_time">
              {convertInRequiredTimeZone(
                item?.created_at,
                item?.medicalUserTimezone,
                currentTimeZone
              )}
            </p>
          </div>
        </div>
        <ArrowRight color="#000000" />
      </div>

      <div
        className={`info_bg ${show ? "alert_detail_show" : "alert_detail_hide"
          }`}
        onClick={() => setOpenInfoModal(true)}
      >
        <IconInfo color={"#FFFFFF"} />
      </div>
      <div
        className={`view_bg ${show ? "alert_detail_show" : "alert_detail_hide"
          }`}
        onClick={() => onNotificationClick(item)}
      >
        <EyeFill color={"#FFFFFF"} width="20" height="20" />
      </div>
            <div
        className={`device_bg ${show ? "alert_detail_show" : "alert_detail_hide"
          }`}
        onClick={() => setOpenDeviceIdsModal(true)}
      >
        <DeviceIdsIcon />
      </div>
    </div>
  );
};

export default Alert;
